import { Link, useNavigate } from 'react-router-dom';
import { Package, ChevronRight, Truck,Download, RotateCcw, Star, Calendar, FileText, HelpCircle, Loader2, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { OrderTimeline } from '@/components/orders/OrderTimeline';
import { OrderSupportCard } from '@/components/orders/OrderSupportCard';
import { authFetch } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useToast } from '@/hooks/use-toast';
import { API_BASE_URL } from '@/config/api';
import { ApiOrder, ApiOrderItem, OrderStatusHistory } from '@/types';

// Helper component for order status timeline
const OrderStatusTimeline = ({ orderId, visible }: { orderId: string; visible: boolean }) => {
  const [history, setHistory] = useState<OrderStatusHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!visible || loaded) return;

    let cancelled = false;
    setLoading(true);

    authFetch(`/api/orders/${orderId}/status-history`)
      .then(r => r.ok ? r.json() : [])
      .then((data: OrderStatusHistory[]) => {
        if (!cancelled) {
          setHistory(data);
          setLoaded(true);
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [visible, orderId, loaded]);

  if (!visible) return null;

  if (loading) {
    return (
      <div className="py-3 flex justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (!history.length) return null;

  return (
    <div className="mt-4 space-y-3">
      {history.map((h, idx) => (
        <div key={idx} className="flex items-start gap-3">
          <div className="flex flex-col items-center">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            {idx !== history.length - 1 && (
              <div className="w-0.5 h-4 bg-gray-200 mt-1" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{h.status}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(h.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
            {h.comment && (
              <p className="text-xs text-muted-foreground mt-1">{h.comment}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const OrdersPage = () => {
  const [orders, setOrders] = useState<ApiOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { toast } = useToast();

  // Map API order status to OrderTimeline format
  const mapOrderStatus = (apiStatus: string): 'processing' | 'shipped' | 'delivered' | 'cancelled' => {
    const status = apiStatus.toUpperCase();
    switch (status) {
      case 'DELIVERED':
        return 'delivered';
      case 'SHIPPED':
      case 'OUT_FOR_DELIVERY':
        return 'shipped';
      case 'CANCELLED':
      case 'CANCELED':
        return 'cancelled';
      case 'CREATED':
      case 'PAID':
      case 'CONFIRMED':
      default:
        return 'processing';
    }
  };

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    if (refreshing) return;
    
    setLoading(true);
    setError(null);

    try {
      // Only fetch orders if user has a customer ID for security
      if (!user?.isAuthenticated || !user.customerId) {
        throw new Error('Please sign in to view your orders');
      }

      const resp = await authFetch(`/api/orders/customer/${user.customerId}`);

      if (!resp.ok) throw new Error('Failed to load orders');

      const data: ApiOrder[] = await resp.json();
      const sorted = [...data].sort(
        (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );

      setOrders(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your orders. Please try again.');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, [user, refreshing]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Show authentication message if user is not authenticated or doesn't have customer ID
  if (!user?.isAuthenticated || !user?.customerId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
            <Package className="h-10 w-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Sign in to view orders</h1>
          <p className="text-muted-foreground mb-8">Please sign in with your account to access your order history.</p>
          <Link to="/auth/signin">
            <Button variant="cta">Sign In</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Refresh orders
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders();
    setRefreshing(false);
  };

  // Helper functions
  const formatDate = (d: string) => {
    return d
      ? new Date(d).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';
  };

  const getItemImage = (item: ApiOrderItem): string => {
    return (
      item.primaryImageUrl ||
      item.imageUrl ||
      item.images?.[0]?.image_url ||
      item.images?.[0]?.imageUrl ||
      'https://placehold.co/100x120/E53935/FFFFFF?text=Joyaura'
    );
  };

  const canDownloadInvoice = (status: string = '') => {
    const s = status.toString().trim().toUpperCase();
    return [
      'PAID',
      'CONFIRMED', 
      'SHIPPED',
      'OUT_FOR_DELIVERY',
      'DELIVERED',
    ].includes(s);
  };

  // Handle download invoice
  const handleDownloadInvoice = async (orderId: string) => {
    try {
      const response = await authFetch(`/api/orders/${orderId}/invoice`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Invoice Downloaded',
        description: 'Your invoice has been downloaded successfully.',
      });
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast({
        title: 'Download Failed',
        description: 'Unable to download invoice. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle reorder
  const handleReorder = async (order: ApiOrder) => {
    try {
      for (const item of order.items) {
        await addToCart({
          variantId: item.variantId,
          productId: item.productId,
          productName: item.productName,
          salePrice: item.price,
          originalPrice: item.originalPrice || item.price,
          size: item.sizeName || item.size || 'N/A',
          color: item.color || 'N/A',
          sku: item.sku,
          seller: {
            id: item.seller?.id || null,
            shopName: item.seller?.shopName || 'Unknown',
            city: item.seller?.city || '',
            state: item.seller?.state || '',
          },
          images: [{
            id: 1,
            imageUrl: getItemImage(item),
            imageType: 'PRIMARY',
            displayOrder: 1,
          }],
        });
      }
      navigate('/cart');
      toast({
        title: 'Items Added to Cart',
        description: 'All items from this order have been added to your cart.',
      });
    } catch (error) {
      console.error('Error reordering:', error);
      toast({
        title: 'Reorder Failed',
        description: 'Unable to reorder items. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Navigate to product detail
  const goToPDPFromOrder = (item: ApiOrderItem) => {
    navigate(`/product/${item.productId}`, {
      state: { variantId: item.variantId },
    });
  };

  // Handle help/support
  const handleHelpCall = (orderId: string) => {
    const message = `Hi I need help with Order ID ${orderId}`;
    const whatsappUrl = `https://wa.me/919940690888?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading && !refreshing) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your orders...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-destructive/10 flex items-center justify-center">
            <Package className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Failed to Load Orders</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={handleRefresh} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  if (orders.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
            <Package className="h-10 w-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-4">No orders yet</h1>
          <p className="text-muted-foreground mb-8">Your order history will appear here.</p>
          <Link to="/">
            <Button variant="cta">Start Shopping</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold">My Orders</h1>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Link to="/" className="text-primary text-sm font-medium flex items-center gap-1">
              Continue Shopping
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {orders.map((order, index) => {
            const expanded = expandedOrderId === order.orderId;
            
            return (
              <motion.div
                key={order.orderId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl shadow-card overflow-hidden"
              >
                {/* Order Header */}
                <div className="p-4 bg-secondary/50 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm md:text-base flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      #{order.orderId}
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3" />
                      Placed on {formatDate(order.createdDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-bold text-primary">₹{order.totalAmount || order.total}</p>
                  </div>
                </div>

                {/* Order Timeline (Metro Style) */}
                <OrderTimeline status={mapOrderStatus(order.status)} orderDate={order.createdDate} />

                {/* Order Items */}
                <div className="p-4 space-y-4">
                  {order.items?.map((item: ApiOrderItem, idx: number) => (
                    <div key={idx} className="flex gap-3 md:gap-4">
                      <button
                        onClick={() => goToPDPFromOrder(item)}
                        className="shrink-0"
                      >
                        <img 
                          src={getItemImage(item)} 
                          alt={item.productName}
                          className="w-16 h-20 md:w-20 md:h-24 object-cover rounded-lg bg-secondary hover:opacity-80 transition-opacity"
                        />
                      </button>
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => goToPDPFromOrder(item)}
                          className="text-left hover:text-primary transition-colors"
                        >
                          <h3 className="font-medium text-sm md:text-base line-clamp-2">
                            {item.productName}
                          </h3>
                        </button>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1">
                          Size: {item.sizeName || item.size || '—'} • Color: {item.color || '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Qty: {item.quantity || 1}
                        </p>
                        <p className="font-semibold text-primary mt-2 text-sm md:text-base">
                          ₹{(item.price * (item.quantity || 1)).toFixed(0)}
                        </p>

                        {order.status === 'DELIVERED' && (
                          <button
                            onClick={() => goToPDPFromOrder(item)}
                            className="flex items-center gap-2 mt-2 text-xs text-yellow-600 hover:text-yellow-700"
                          >
                            <Star className="h-3 w-3" />
                            Rate & Review
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="px-4 pb-4">
                  <div className="flex gap-2 mb-4">
                    <Button
                      onClick={() => handleReorder(order)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reorder
                    </Button>

                    {canDownloadInvoice(order.status) && (
                      <Button
                        onClick={() => handleDownloadInvoice(order.orderId)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Invoice
                      </Button>
                    )}
                  </div>

                  {/* Track Order Toggle */}
                  <button
                    onClick={() => setExpandedOrderId(expanded ? null : order.orderId)}
                    className="w-full flex items-center justify-between p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-primary" />
                      <span className="font-medium text-primary">Track Order</span>
                    </div>
                    {expanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>

                  <OrderStatusTimeline orderId={order.orderId} visible={expanded} />

                  {/* Help Section */}
                  <button
                    onClick={() => handleHelpCall(order.orderId)}
                    className="w-full flex items-center justify-between p-3 mt-4 border-t border-border hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">Need help with this order?</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default OrdersPage;
